/**
 * CSS overlay coordinates for the interactive de minimis form.
 *
 * Converted from pdf-lib coordinates in:
 *   apps/backend/src/grants/forms/de-minimis-field-coords.ts
 *
 * Conversion from pdf-lib (origin=bottom-left) to CSS % (origin=top-left):
 *   PAGE_WIDTH  = 595.28 (A4 width in points)
 *   PAGE_HEIGHT = 841.92 (A4 height in points)
 *
 *   CSS left%   = (field.x / PAGE_WIDTH) * 100
 *   CSS top%    = ((PAGE_HEIGHT - field.y - field.height) / PAGE_HEIGHT) * 100
 *   CSS width%  = (field.width / PAGE_WIDTH) * 100
 *   CSS height% = (field.height / PAGE_HEIGHT) * 100
 *
 * Digit fields (NIP, TERYT, PKD, dates) export individual CssRect arrays
 * so that each digit gets its own input box.
 *
 * For checkboxes, a small padding is added for touch targets.
 */

// =========================================================================
// Helpers
// =========================================================================

const PW = 595.28;
const PH = 841.92;

export interface CssRect {
  top: string;
  left: string;
  width: string;
  height: string;
}

/** Convert a single pdf-lib FieldBox to CSS % rect. */
function css(x: number, y: number, w: number, h: number): CssRect {
  return {
    left: `${((x / PW) * 100).toFixed(2)}%`,
    top: `${(((PH - y - h) / PH) * 100).toFixed(2)}%`,
    width: `${((w / PW) * 100).toFixed(2)}%`,
    height: `${((h / PH) * 100).toFixed(2)}%`,
  };
}

/** Convert each digit box to an individual CSS rect. */
function digitBoxesCss(
  boxes: Array<{ x: number; y: number; w: number; h: number }>,
): CssRect[] {
  return boxes.map(({ x, y, w, h }) => css(x, y, w, h));
}

/**
 * Convert a checkbox pdf-lib box to a CSS rect matching the exact PDF square.
 * No padding - the button should fit exactly inside the PDF checkbox border.
 */
function checkboxCss(x: number, y: number, w: number, h: number): CssRect {
  return css(x, y, w, h);
}

// =========================================================================
// Backend box() helper reproduces pdfplumber -> pdf-lib conversion.
// We inline the results to avoid importing backend code.
//
// box(x0, top, x1, bottom) =>
//   { x: x0, y: PH - bottom, width: x1 - x0, height: bottom - top }
//
// checkboxBox(x0, yFromTop) with offset 9 =>
//   squareTop = yFromTop - 9, squareBottom = squareTop + 13
//   { x: x0 + 1, y: PH - squareBottom, width: 13, height: 13 }
//
// checkboxBoxGothic(x0, yFromTop) with offset -1 =>
//   squareTop = yFromTop + 1, squareBottom = squareTop + 13
//   { x: x0 + 1, y: PH - squareBottom, width: 13, height: 13 }
// =========================================================================

// Shorthand for box() results as { x, y, w, h }
function b(x0: number, top: number, x1: number, bottom: number) {
  return { x: x0, y: PH - bottom, w: x1 - x0, h: bottom - top };
}

// Shorthand for checkboxBox (SegoeUISymbol, offset=9)
function cb(x0: number, yFromTop: number): CssRect {
  const squareTop = yFromTop - 9;
  const squareBottom = squareTop + 13;
  return checkboxCss(x0 + 1, PH - squareBottom, 13, 13);
}

// Shorthand for checkboxBoxGothic (MS-Gothic, offset=-1)
function cbg(x0: number, yFromTop: number): CssRect {
  const squareTop = yFromTop + 1;
  const squareBottom = squareTop + 13;
  return checkboxCss(x0 + 1, PH - squareBottom, 13, 13);
}

// =========================================================================
// PAGE 1
// =========================================================================

export const PAGE1_COORDS = {
  /** A.1 - NIP podmiotu: 10 individual digit boxes */
  nipDigits: digitBoxesCss([
    b(62.9, 190.94, 85.46, 214.1),
    b(85.94, 190.94, 107.18, 214.1),
    b(107.66, 190.94, 128.78, 214.1),
    b(129.26, 190.94, 150.38, 214.1),
    b(150.86, 190.94, 171.98, 214.1),
    b(172.46, 190.94, 193.6, 214.1),
    b(194.09, 190.94, 215.21, 214.1),
    b(215.69, 190.94, 236.81, 214.1),
    b(237.29, 190.94, 258.41, 214.1),
    b(258.89, 190.94, 280.01, 214.1),
  ]),

  /** A1a - NIP wnioskodawcy (right column): 10 individual digit boxes */
  nipWnioskodawcyDigits: digitBoxesCss([
    b(315.79, 189.98, 338.35, 213.14),
    b(338.83, 189.98, 360.07, 213.14),
    b(360.55, 189.98, 381.67, 213.14),
    b(382.15, 189.98, 403.27, 213.14),
    b(403.75, 189.98, 424.87, 213.14),
    b(425.35, 189.98, 446.49, 213.14),
    b(446.98, 189.98, 468.1, 213.14),
    b(468.58, 189.98, 489.7, 213.14),
    b(490.18, 189.98, 511.3, 213.14),
    b(511.78, 189.98, 532.9, 213.14),
  ]),

  /** A.2 - Nazwa podmiotu */
  nazwa: css(62.42, PH - 271.94, 304.48 - 62.42, 271.94 - 246.5),

  /** A1.2a - Nazwa wnioskodawcy (right column) */
  nazwaWnioskodawcy: css(315.79, PH - 271.94, 532.9 - 315.79, 271.94 - 246.5),

  /** A.3 - Adres */
  adres: css(62.42, PH - 353, 304.48 - 62.42, 353 - 305),

  /** A1.3a - Adres wnioskodawcy (right column) */
  adresWnioskodawcy: css(315.79, PH - 353.0, 532.9 - 315.79, 353.0 - 305.0),

  /** A.4 - Identyfikator gminy TERYT: 7 individual digit boxes */
  gminaDigits: digitBoxesCss([
    b(62.9, 387.05, 86.18, 408.41),
    b(86.66, 387.05, 108.38, 408.41),
    b(108.86, 387.05, 130.58, 408.41),
    b(131.06, 387.05, 152.78, 408.41),
    b(153.26, 387.05, 174.98, 408.41),
    b(175.46, 387.05, 197.33, 408.41),
    b(197.81, 387.05, 219.53, 408.41),
  ]),

  /** A.5 - Forma prawna checkboxes (6 items) */
  formaPrawna: [
    cb(513.58, 460.89), // 0: przedsiebiorstwo panstwowe
    cb(513.58, 487.31), // 1: jednoosobowa sp. Skarbu Panstwa
    cb(513.58, 515.27), // 2: jednoosobowa sp. JST
    cb(513.58, 569.87), // 3: podmiot dominujacy
    cb(513.58, 626.27), // 4: sektor finansow publicznych
    cb(513.58, 657.38), // 5: inna
  ],

  /** A.5 - "inna (podac jaka)" text field */
  formaPrawnaInna: css(62.42, PH - 697.54, 482.5 - 62.42, 697.54 - 669.22),

  /** A.6 - Wielkosc podmiotu (first 2 on page 1) */
  wielkoscMikro: cb(312.53, 760.58),
  wielkoscSredni: cb(523.9, 760.58),
};

// =========================================================================
// PAGE 2
// =========================================================================

export const PAGE2_COORDS = {
  /** A.6 continued - remaining 2 checkboxes */
  wielkoscMaly: cb(312.53, 52.12),
  wielkoscInny: cb(523.9, 52.12),

  /** A.7 - PKD: 4 individual digit boxes */
  pkdDigits: digitBoxesCss([
    b(62.42, 96.38, 86.18, 117.86),
    b(86.18, 96.38, 108.38, 117.86),
    b(108.38, 96.38, 130.58, 117.86),
    b(130.58, 96.38, 152.78, 117.86),
  ]),

  /** A.7a - PKD 2024 checkbox */
  pkd2024: cb(523.9, 143.46),

  /** A.7b - PKD 2007 checkbox */
  pkd2007: cb(523.9, 180.9),

  /** A.8 - Data utworzenia: DD-MM-YYYY as 8 individual digit boxes */
  dataUtworzeniaDigits: digitBoxesCss([
    b(62.9, 232.46, 85.1, 254.18),    // D
    b(85.58, 232.46, 106.46, 254.18), // D
    b(128.18, 232.46, 148.94, 254.18), // M
    b(149.42, 232.46, 170.18, 254.18), // M
    b(192.05, 232.46, 212.81, 254.18), // Y
    b(213.29, 232.46, 234.05, 254.18), // Y
    b(234.53, 232.46, 255.29, 254.18), // Y
    b(255.77, 232.46, 276.53, 254.18), // Y
  ]),

  /** A.9 - Powiazania: 5 tak/nie pairs */
  sekcja9: [
    {
      tak: cbg(334.27, 314.86),
      nie: cbg(475.18, 314.86),
    }, // 9a (MS-Gothic)
    { tak: cb(337.15, 382.05), nie: cb(476.5, 382.05) }, // 9b
    { tak: cb(337.15, 452.01), nie: cb(476.5, 452.01) }, // 9c
    {
      tak: cbg(335.95, 532.56),
      nie: cb(476.5, 545.15),
    }, // 9d (tak=MS-Gothic)
    { tak: cb(337.15, 630.59), nie: cb(476.5, 630.59) }, // 9e
  ],
};

// =========================================================================
// PAGE 3
// =========================================================================

export const PAGE3_COORDS = {
  /** A.10 - Polaczenie/podzial: 4 tak/nie pairs */
  sekcja10: [
    { tak: cb(337.15, 248.7), nie: cb(476.5, 248.7) }, // 10a
    { tak: cb(337.15, 278.37), nie: cb(476.5, 278.37) }, // 10b
    { tak: cb(337.15, 308.13), nie: cb(476.5, 308.13) }, // 10c
    { tak: cb(337.15, 339.45), nie: cb(476.5, 339.45) }, // 10d
  ],

  /** Section 9 conditional - NIP of related entities (right column, measured y: 53.7-86.8) */
  sekcja9NipPowiazanych: css(284.0, PH - 86.8, 557.0 - 284.0, 86.8 - 53.7),

  /** Section 9 conditional - Total de minimis aid value (right column, measured y: 86.8-167.9) */
  sekcja9WartoscPomocy: css(284.0, PH - 167.9, 557.0 - 284.0, 167.9 - 86.8),

  /** Section 10a/b conditional - NIP of merged/acquired entities (measured y: 368.9-400.1) */
  sekcja10abNipPolaczonych: css(284.0, PH - 400.1, 557.0 - 284.0, 400.1 - 368.9),

  /** Section 10a/b conditional - Total aid value (measured y: 400.1-477.8) */
  sekcja10abWartoscPomocy: css(284.0, PH - 477.8, 557.0 - 284.0, 477.8 - 400.1),

  /** Section 10c/d conditional - NIP of entity before split (row a: y=498-525) */
  sekcja10cdNipPrzedPodzialem: css(284.0, PH - 525.0, 557.0 - 284.0, 525.0 - 498.0),

  /** Section 10c/d conditional - Total aid value (row b: y=525-640) */
  sekcja10cdWartoscPomocy: css(284.0, PH - 640.0, 557.0 - 284.0, 640.0 - 525.0),

  /** Section 10c/d conditional - Total aid value before split (large cell, y: 690-742) */
  sekcja10cdWartoscPrzedPodzialem: css(284.0, PH - 742.0, 557.0 - 284.0, 742.0 - 690.0),

  /** Section 10c/d conditional - Capital value before split (PLN) (measured y: 742.5-774.2) */
  sekcja10cdKapitalPrzed: css(284.0, PH - 774.2, 557.0 - 284.0, 774.2 - 742.5),
};

// =========================================================================
// PAGE 4
// =========================================================================

export const PAGE4_COORDS = {
  /** Section 10c/d conditional - Capital value at the moment of split (PLN) (measured y: 36.0-67.2) */
  sekcja10cdKapitalMoment: css(284.0, PH - 67.2, 557.0 - 284.0, 67.2 - 36.0),

  /** B.1 - Upadlosc/restrukturyzacja */
  sekcjaB1: {
    tak: cb(62.42, 130.86),
    nie: cb(203.93, 130.86),
  },

  /** B.2 - Rating kredytowy (tri-state) */
  sekcjaB2: {
    tak: cb(62.42, 236.58),
    nie: cb(203.93, 236.58),
    nieDotyczy: cb(353.23, 236.58),
  },

  /** B.3 - Sytuacja ekonomiczna: 9 tak/nie pairs */
  sekcjaB3: [
    { tak: cb(287.09, 285.45), nie: cb(427.63, 285.45) }, // a
    { tak: cb(287.09, 313.53), nie: cb(427.63, 313.53) }, // b
    { tak: cb(287.09, 350.97), nie: cb(427.63, 350.97) }, // c
    { tak: cb(287.09, 388.41), nie: cb(427.63, 388.41) }, // d
    {
      tak: cbg(287.09, 405.46),
      nie: cbg(427.63, 405.46),
    }, // e (MS-Gothic)
    { tak: cb(287.09, 447.81), nie: cb(427.63, 447.81) }, // f
    { tak: cb(287.09, 477.47), nie: cb(427.63, 477.47) }, // g
    { tak: cb(287.09, 508.91), nie: cb(427.63, 508.91) }, // h
    { tak: cb(287.09, 545.27), nie: cb(427.63, 545.27) }, // i
  ],

  /** B.3i conditional - Description of financial difficulties (input area y: 583-628 pdfplumber) */
  sekcjaB3iOpis: css(62.42, PH - 628.0, 533.0 - 62.42, 628.0 - 583.0),

  /** C.1a - Rybolostwo/akwakultura */
  sekcjaC1a: {
    tak: cb(287.09, 713.9),
    nie: cb(427.63, 714.38),
  },
};

// =========================================================================
// PAGE 5
// =========================================================================

export const PAGE5_COORDS = {
  /** C.1b - Produkty rolne */
  sekcjaC1b: {
    tak: cb(287.09, 69.28),
    nie: cb(427.63, 69.28),
  },

  /** C.2 - Pomoc na dzialalnosc */
  sekcjaC2: {
    tak: cb(62.42, 145.86),
    nie: cb(203.93, 145.86),
  },

  /** C.3 - Rozdzielnosc rachunkowa (tri-state) */
  sekcjaC3: {
    tak: cb(62.42, 220.62),
    nie: cb(203.93, 220.62),
    nieDotyczy: cb(353.23, 220.62),
  },

  /** C.3 conditional - Description of accounting separation (input area y: 238-302 pdfplumber) */
  sekcjaC3Opis: css(62.42, PH - 302.0, 533.0 - 62.42, 302.0 - 238.0),

  /** D.1 - Koszty kwalifikowalne */
  sekcjaD1: {
    tak: cb(62.42, 384.09),
    nie: cb(203.93, 384.09),
  },

  /** D.2 - Inna pomoc na te same koszty (tri-state) */
  sekcjaD2: {
    tak: cb(62.42, 443.49),
    nie: cb(203.93, 443.49),
    nieDotyczy: cb(353.23, 443.49),
  },

  /**
   * D.3 - Table of received aid.
   *
   * Container covering ONLY the 5 data rows (not header or column numbers row).
   * Matches backend D3_TABLE_P5.rows: y_top=536, y_bottom=656 (pdfplumber coords).
   * Columns: dzienUdzielenia, podmiotUdzielajacy, podstawaPrawna,
   * przeznaczenie, formaPomocy, wartoscBrutto.
   * Lp column is auto-generated (not an input).
   */
  d3Table: {
    left: "14.61%",
    top: "67.53%",
    width: "78.84%",
    height: "11.05%",
  },

  /** D.3 table row y-positions (CSS top%) - 4 rows (row 1 removed, overlaps column numbers) */
  d3RowTops: [
    `${(((PH - 577.2 - 23.2) / PH) * 100).toFixed(2)}%`, // row 1 (was row 2): 577-600
    `${(((PH - 600.4 - 23.2) / PH) * 100).toFixed(2)}%`, // row 2 (was row 3): 600-624
    `${(((PH - 623.6 - 23.2) / PH) * 100).toFixed(2)}%`, // row 3 (was row 4): 624-647
    `${(((PH - 646.8 - 23.2) / PH) * 100).toFixed(2)}%`, // row 4 (was row 5): 647-670
  ],

  /** D.3 table column positions - adjusted widths from screenshot feedback */
  d3Columns: [
    css(90.0, 0, 168.0 - 90.0, 24.0),   // dzienUdzielenia (OK)
    css(168.0, 0, 256.0 - 168.0, 24.0),  // podmiotUdzielajacy (OK)
    css(256.0, 0, 359.0 - 256.0, 24.0),  // podstawaPrawna (+15pt wider)
    css(359.0, 0, 438.0 - 359.0, 24.0),  // przeznaczenie (shifted right)
    css(438.0, 0, 522.0 - 438.0, 24.0),  // formaPomocy (+20pt right)
    css(522.0, 0, 573.0 - 522.0, 24.0),  // wartoscBrutto (+40pt right)
  ],

  /**
   * D.4a - Opis przedsiewziecia (description of the undertaking).
   *
   * Input area starts BELOW the "a) opis przedsiewziecia" label (~y=700 pdfplumber)
   * and extends to the bottom of the available space (~y=768).
   * Previous y_top=672 overlapped with "4. Jezeli w tabeli wykazano..." text.
   */
  opisPrzedsiewziecia: css(62.42, PH - 768.0, 533.0 - 62.42, 768.0 - 713.0),
};

// =========================================================================
// PAGE 6
// =========================================================================

export const PAGE6_COORDS = {
  /** D.4b - Koszty kwalifikujace sie do objecia pomoca (measured from PNG) */
  d4bKoszty: css(62.42, PH - 83.5, 533.0 - 62.42, 83.5 - 60.0),

  /** D.4c - Maksymalna dopuszczalna intensywnosc pomocy */
  d4cIntensywnosc: css(62.42, PH - 131.5, 533.0 - 62.42, 131.5 - 107.5),

  /** D.4d - Intensywnosc pomocy juz udzielonej */
  d4dIntensywnoscUdzielona: css(62.42, PH - 179.5, 533.0 - 62.42, 179.5 - 155.5),

  /** D.4e - Lokalizacja przedsiewziecia */
  d4eLokalizacja: css(62.42, PH - 231.0, 533.0 - 62.42, 231.0 - 203.5),

  /** D.4f - Cele do osiagniecia */
  d4fCele: css(62.42, PH - 278.4, 533.0 - 62.42, 278.4 - 254.4),

  /** D.4g - Etapy realizacji przedsiewziecia */
  d4gEtapy: css(62.42, PH - 326.4, 533.0 - 62.42, 326.4 - 302.4),

  /** D.4h - Data rozpoczecia i zakonczenia */
  d4hDaty: css(62.42, PH - 374.0, 533.0 - 62.42, 374.0 - 350.4),

  /** E - Data wypelnienia: DD-MM-YYYY as 8 individual digit boxes */
  dataDigits: digitBoxesCss([
    b(62.9, 429.41, 85.1, 451.25),    // D
    b(85.58, 429.41, 106.46, 451.25), // D
    b(128.18, 429.41, 148.94, 451.25), // M
    b(149.42, 429.41, 170.18, 451.25), // M
    b(192.05, 429.41, 212.81, 451.25), // Y
    b(213.29, 429.41, 234.05, 451.25), // Y
    b(234.53, 429.41, 255.29, 451.25), // Y
    b(255.77, 429.41, 276.53, 451.25), // Y
  ]),

  /** E - Stanowisko sluzbowe: input in EMPTY cell BELOW the label cell (502-530) */
  stanowisko: css(62.42, PH - 527.0, 554.61 - 62.42, 527.0 - 505.0),

  /** E - Numer telefonu: input in EMPTY cell BELOW the label cell (558-587) */
  telefon: css(62.42, PH - 583.0, 554.61 - 62.42, 583.0 - 561.0),

  /** E - Imie, nazwisko i podpis: input in cell below label (~615-635) */
  imieNazwisko: css(62.42, PH - 635.0, 554.61 - 62.42, 635.0 - 615.0),
};
